'use client';

import { useEffect, useRef, useState } from 'react';
import { PauseIcon, PlayIcon } from '@phosphor-icons/react/ssr';

/**
 * The home hero's product loop: a muted 14s video of the app (book a ride,
 * compare fares, track it live, share the trip), centred in the hero beside
 * the text. Its background is ink-950, so it blends into the band.
 *
 * The poster is the LCP candidate and the only thing shown to visitors who
 * prefer reduced motion: the video is fetched only when it is about to play
 * (`preload="none"`), and it plays only without that preference. It loops
 * longer than 5s, so it has a pause control (WCAG 2.2.2).
 */
export default function HeroLoop({ caption }: { caption?: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    // React sets `muted` as a property only, so set it before asking to play.
    video.muted = true;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => {
      if (reduce.matches) video.pause();
      else video.play().catch(() => {});
    };
    sync();
    reduce.addEventListener('change', sync);
    return () => reduce.removeEventListener('change', sync);
  }, []);

  const toggle = () => {
    const video = ref.current;
    if (!video) return;
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  };

  return (
    <figure className="relative mx-auto aspect-square w-full max-w-[34rem] overflow-hidden">
      <video
        ref={ref}
        muted
        loop
        playsInline
        preload="none"
        poster="/assets/hero-loop-poster.jpg"
        width={1080}
        height={1080}
        aria-hidden="true"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        className="absolute inset-0 size-full"
      >
        <source src="/assets/hero-loop.webm" type="video/webm" />
        <source src="/assets/hero-loop.mp4" type="video/mp4" />
      </video>
      <p className="sr-only">
        A short animation of the Point2 app: setting a destination, comparing ride options and fares, following the
        driver on the map, and sharing the trip.
      </p>
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? 'Pause animation' : 'Play animation'}
        className="focus-ring-on-ink absolute top-3 right-3 grid size-11 place-items-center rounded-full bg-white/10 text-on-ink ring-1 ring-white/20 ring-inset transition-colors hover:bg-white/15"
      >
        {playing ? <PauseIcon size={20} weight="fill" aria-hidden="true" /> : <PlayIcon size={20} weight="fill" aria-hidden="true" />}
      </button>
      <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-ink-950 to-transparent" />
      {caption ? (
        <figcaption className="absolute inset-x-0 bottom-3 text-center text-[13px] text-on-ink-muted">{caption}</figcaption>
      ) : null}
    </figure>
  );
}
